import type { ReactFlowCanvasEdge } from '../../hooks/useYCanvas'

/** A downstream input port that lost an edge from a removed node. */
export type AffectedDownstream = {
  target: string
  targetHandle: string | undefined
}

/**
 * For each removed node, collect its outgoing edges' downstream ports.
 *
 * Used by the port-flush path in ``onNodesChange`` (v2 Canvas):
 * after the removal cascades through ReactFlow + YDoc, every
 * ``(target, targetHandle)`` in this list needs its resolved input
 * value refreshed from whatever edges remain (or cleared if no edges
 * remain). Without the flush, downstream nodes keep showing the
 * deleted upstream's stale resolved data until the next rebuild().
 *
 * Semantics:
 *  - Only edges whose ``source`` is in ``removedIds`` are considered
 *    (we don't care about edges into removed nodes — those nodes are
 *    going away).
 *  - Edges where ``target`` is ALSO in ``removedIds`` are skipped
 *    (the target is being removed too, so there's nothing to flush).
 *  - The output is de-duplicated by ``(target, targetHandle)`` —
 *    multiple removed upstreams can feed the same multi-input port,
 *    but the flush is idempotent so one entry per port is enough.
 *  - ``targetHandle`` is normalised to ``undefined`` when null/missing
 *    so the de-dup key is stable. ``''`` is preserved as a valid handle.
 */
export function collectDownstreamAffectedByRemoval(
  removedIds: ReadonlyArray<string>,
  edges: ReadonlyArray<ReactFlowCanvasEdge>,
): AffectedDownstream[] {
  if (removedIds.length === 0) return []
  const removed = new Set(removedIds)
  const seen = new Set<string>()
  const out: AffectedDownstream[] = []
  for (const e of edges) {
    if (!removed.has(e.source) || removed.has(e.target)) continue
    const handle = e.targetHandle ?? undefined
    const key = `${e.target}|${handle ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ target: e.target, targetHandle: handle })
  }
  return out
}
